import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReviewSection from '../components/ReviewSection';
import { useAppStore } from '../services/store';

export default function AppDetails() {
  const { appId } = useParams<{ appId: string }>();
  const [activeTab, setActiveTab] = useState('overview');
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [imageError, setImageError] = useState(false);

  const { getAppById, installApp, loadInitialData } = useAppStore();
  const app = appId ? getAppById(appId) : undefined;

  useEffect(() => {
    if (!app) {
      loadInitialData();
    }
  }, [app, loadInitialData]);

  if (!app) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-6 py-16 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-2xl text-gray-400"></i>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">App Not Found</h1>
          <p className="text-gray-600 mb-6">The app you're looking for doesn't exist or has been removed.</p>
          <Link
            to="/app/appstore/apps"
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap inline-block"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Store
          </Link>
        </div>
      </div>
    );
  }

  const handleActionClick = () => {
    if (app.status === 'Installed') {
      // Launch app - use the actual launchUrl or show a message
      if (app.launchUrl && app.launchUrl !== '#') {
        window.open(app.launchUrl, '_blank');
      } else {
        alert(`Launching ${app.name}...`);
      }
    } else {
      installApp(app.id);
    }
  };

  const getActionButton = () => {
    switch (app.status) {
      case 'Installed':
        return (
          <button
            onClick={handleActionClick}
            className="bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 transition-colors font-semibold whitespace-nowrap"
          >
            <i className="ri-play-line mr-2"></i>
            Launch App
          </button>
        );
      case 'Not Installed':
        if (app.accessType === 'Paid') {
          return (
            <button
              onClick={handleActionClick}
              className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold whitespace-nowrap"
            >
              <i className="ri-shopping-cart-line mr-2"></i>
              Purchase
            </button>
          );
        } else if (app.accessType === 'Free Trial') {
          return (
            <button
              onClick={handleActionClick}
              className="bg-purple-600 text-white px-8 py-3 rounded-lg hover:bg-purple-700 transition-colors font-semibold whitespace-nowrap"
            >
              <i className="ri-download-line mr-2"></i>
              Start Free Trial
            </button>
          );
        } else {
          return (
            <button
              onClick={handleActionClick}
              className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold whitespace-nowrap"
            >
              <i className="ri-download-line mr-2"></i>
              Install Free
            </button>
          );
        }
      default:
        return null;
    }
  };

  const getAccessTypeColor = () => {
    switch (app.accessType) {
      case 'Free':
        return 'bg-green-100 text-green-800';
      case 'Free Trial':
        return 'bg-purple-100 text-purple-800';
      case 'Paid':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <div className="mb-6">
          <Link to="/app/appstore/apps" className="text-blue-600 hover:text-blue-700 text-sm flex items-center whitespace-nowrap">
            <i className="ri-arrow-left-line mr-1"></i>
            Back to Store
          </Link>
        </div>

        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          {!imageError && app.coverImage && (
            <div className="relative h-64 bg-gradient-to-r from-blue-600 to-purple-600">
              <img
                src={app.coverImage}
                alt={`${app.name} cover`}
                className="w-full h-full object-cover object-top"
                onError={() => setImageError(true)}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
            </div>
          )}

          <div className="p-8 border-b">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0">
                <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <i className="ri-apps-2-line text-white text-3xl"></i>
                </div>
              </div>

              <div className="flex-1">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
                  <div>
                    <h1 className="text-4xl font-bold text-gray-900 mb-3">{app.name}</h1>
                    <p className="text-xl text-gray-600 mb-4 leading-relaxed">{app.description}</p>
                    <div className="flex items-center gap-4 mb-4">
                      <span className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${getAccessTypeColor()}`}>
                        {app.accessType}
                      </span>
                      <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{app.category}</span>
                      {app.isHighlighted && (
                        <span className="bg-yellow-100 text-yellow-800 text-sm px-3 py-1 rounded-full font-medium whitespace-nowrap">
                          <i className="ri-star-fill mr-1"></i>
                          Featured
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    {getActionButton()}
                    {app.status === 'Installed' && app.accessType === 'Free Trial' && (
                      <button className="border-2 border-blue-600 text-blue-600 px-8 py-3 rounded-lg hover:bg-blue-50 transition-colors font-semibold whitespace-nowrap">
                        Upgrade to Pro
                      </button>
                    )}
                  </div>
                </div>

                {app.status === 'Installed' && app.trialExpiresAt && (
                  <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="flex items-center">
                      <i className="ri-time-line text-orange-600 mr-2 text-lg"></i>
                      <span className="text-orange-800 font-medium">
                        Trial expires on {new Date(app.trialExpiresAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Info cards - full width */}
            <div className="mt-6 bg-blue-50/50 border border-blue-100 rounded-xl p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <button
                  onClick={() => setActiveTab('reviews')}
                  className="flex items-center gap-3 hover:bg-blue-100/50 rounded-lg p-2 -m-2 transition-colors cursor-pointer text-left"
                >
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <i className="ri-star-fill text-yellow-500"></i>
                  </div>
                  <div>
                    <span className="text-gray-500 text-xs block">Rating</span>
                    <div className="flex items-center">
                      <span className="font-semibold text-gray-800">{app.rating}</span>
                      <span className="text-gray-400 text-xs ml-1">({app.reviewCount})</span>
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('changelog')}
                  className="flex items-center gap-3 hover:bg-blue-100/50 rounded-lg p-2 -m-2 transition-colors cursor-pointer text-left"
                >
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <i className="ri-price-tag-3-line text-green-500"></i>
                  </div>
                  <div>
                    <span className="text-gray-500 text-xs block">Version</span>
                    <span className="font-semibold text-gray-800">{app.version}</span>
                  </div>
                </button>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <i className="ri-hard-drive-2-line text-purple-500"></i>
                  </div>
                  <div>
                    <span className="text-gray-500 text-xs block">Size</span>
                    <span className="font-semibold text-gray-800">{app.size}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <i className="ri-user-line text-blue-500"></i>
                  </div>
                  <div>
                    <span className="text-gray-500 text-xs block">Developer</span>
                    <span className="font-semibold text-gray-800">{app.developer}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-8">
            <div className="flex justify-center mb-8">
              <div className="inline-flex border-2 border-blue-600 rounded-full p-1">
                {[
                  { id: 'overview', label: 'Overview' },
                  { id: 'screenshots', label: 'Screenshots' },
                  { id: 'reviews', label: 'Reviews' },
                  { id: 'changelog', label: 'Changelog' },
                  { id: 'roadmap', label: 'Roadmap' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-5 py-2 rounded-full font-medium transition-all whitespace-nowrap text-sm ${
                      activeTab === tab.id
                        ? 'bg-blue-600 text-white'
                        : 'text-blue-600 hover:bg-blue-50'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="max-w-4xl">
              {activeTab === 'overview' && (
                <div className="space-y-8">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-6">About this app</h3>
                    <div className="prose max-w-none">
                      <p className="text-gray-700 leading-relaxed text-lg">
                        {app.fullDescription && (showFullDescription ? app.fullDescription : `${app.fullDescription.substring(0, 400)}...`)}
                        {app.fullDescription && app.fullDescription.length > 400 && (
                          <button
                            onClick={() => setShowFullDescription(!showFullDescription)}
                            className="text-blue-600 hover:text-blue-700 ml-2 whitespace-nowrap font-medium"
                          >
                            {showFullDescription ? 'Show less' : 'Read more'}
                          </button>
                        )}
                      </p>
                    </div>
                  </div>

                  {app.features && app.features.length > 0 && (
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-6">Key Features</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        {app.features.map((feature, index) => (
                          <div key={index} className="flex items-center bg-green-50 p-3 rounded-lg">
                            <i className="ri-check-line text-green-600 mr-3 text-lg"></i>
                            <span className="text-gray-700 font-medium">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {app.requirements && app.requirements.length > 0 && (
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-6">System Requirements</h3>
                      <div className="bg-gray-50 rounded-lg p-6">
                        <ul className="space-y-3">
                          {app.requirements.map((requirement, index) => (
                            <li key={index} className="flex items-center text-gray-700">
                              <i className="ri-computer-line text-gray-400 mr-3 text-lg"></i>
                              <span className="font-medium">{requirement}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'screenshots' && (
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Screenshots</h3>
                  {app.screenshots && app.screenshots.length > 0 ? (
                    <div className="grid md:grid-cols-2 gap-6">
                      {app.screenshots.map((screenshot, index) => (
                        <div key={index} className="bg-gray-100 rounded-lg overflow-hidden shadow-sm">
                          <img
                            src={screenshot}
                            alt={`${app.name} screenshot ${index + 1}`}
                            className="w-full h-64 object-cover object-top hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <i className="ri-image-line text-4xl text-gray-300 mb-4"></i>
                      <p className="text-gray-500">Screenshots coming soon</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'reviews' && (
                <ReviewSection app={app} />
              )}

              {activeTab === 'changelog' && (
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Version History</h3>
                  {app.changelog && app.changelog.length > 0 ? (
                    <div className="space-y-6">
                      {app.changelog.map((entry, index) => (
                        <div key={index} className="border-l-4 border-blue-200 pl-6 bg-blue-50 p-6 rounded-r-lg">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-bold text-lg text-gray-900">Version {entry.version}</h4>
                            <span className="text-sm text-gray-500 bg-white px-3 py-1 rounded-full">{new Date(entry.date).toLocaleDateString()}</span>
                          </div>
                          <ul className="space-y-2">
                            {entry.changes.map((change, changeIndex) => (
                              <li key={changeIndex} className="text-gray-700 flex items-start">
                                <i className="ri-arrow-right-s-line text-blue-600 mr-2 mt-0.5"></i>
                                <span className="font-medium">{change}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">No changelog available</p>
                  )}
                </div>
              )}

              {activeTab === 'roadmap' && (
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Development Roadmap</h3>
                  {app.roadmap && app.roadmap.length > 0 ? (
                    <div className="space-y-6">
                      {app.roadmap.map((item, index) => (
                        <div key={index} className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg p-6 border">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-bold text-lg text-gray-900">{item.quarter}</h4>
                            <span className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                              item.status === 'In Development' ? 'bg-blue-100 text-blue-800' :
                              item.status === 'Coming Soon' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {item.status}
                            </span>
                          </div>
                          <ul className="space-y-3">
                            {item.features.map((feature, featureIndex) => (
                              <li key={featureIndex} className="text-gray-700 flex items-start">
                                <i className="ri-road-map-line text-blue-600 mr-3 mt-0.5 text-lg"></i>
                                <span className="font-medium">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">No roadmap available</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
